import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [amount, setAmount] = useState(null);
  const { refresh } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 7;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setAmount(data.amount);
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="pt-32 px-6 md:px-12 min-h-screen flex items-center" data-testid="checkout-success-page">
      <div className="max-w-2xl">
        {status === "pending" && (
          <>
            <Loader2 className="animate-spin mb-6" size={32} />
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Confirming your order…</h1>
            <p className="mt-6 text-black/60">Hold tight — we're verifying with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mb-6 text-[#C9A961]" size={40} strokeWidth={1.2} />
            <div className="label-eyebrow mb-3">Order confirmed</div>
            <h1 data-testid="success-headline" className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              Thank you.<br />
              Now we get to work.
            </h1>
            {amount && (
              <p className="mt-8 font-display text-2xl">Total paid: ${amount.toFixed(2)}</p>
            )}
            <p className="mt-6 text-black/60 max-w-md">
              A confirmation will reach your inbox shortly. Each piece is hand-finished — expect 5–9 working days.
            </p>
            <div className="mt-12 flex gap-4">
              <Link to="/shop" className="bg-black text-white px-6 py-3 text-sm">Continue shopping</Link>
              <Link to="/" className="border border-black px-6 py-3 text-sm">Home</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter">Hmm — we couldn't confirm that.</h1>
            <p className="mt-6 text-black/60">{status === "expired" ? "Your session expired." : "Please try again or contact us if charged."}</p>
            <Link to="/cart" className="mt-8 inline-block bg-black text-white px-6 py-3 text-sm">Back to cart</Link>
          </>
        )}
      </div>
    </div>
  );
}
